import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Machine, MachineSearchParams } from '../../services/machineApi';

export interface MachineState {
  // Data
  items: Machine[];
  selectedItem: Machine | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    MachineNumber: string;
    MaintenanceRequired: boolean;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: MachineSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
    maintenanceRequired: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: MachineState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    MachineNumber: '',
    MaintenanceRequired: false,
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    maintenanceRequired: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
    maintenanceRequired: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const machineSlice = createSlice({
  name: 'machine',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<Machine[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<Machine | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<Machine>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<Machine>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedItem?.Id === action.payload.Id) {
        state.selectedItem = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
      if (state.selectedItem?.Id === action.payload) {
        state.selectedItem = null;
      }
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<MachineState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<MachineSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<MachineState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<MachineState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetMachineState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetMachineState,
} = machineSlice.actions;

// Saga action types
export const FETCH_MACHINES = 'machine/fetchMachines';
export const FETCH_MACHINE_BY_ID = 'machine/fetchMachineById';
export const CREATE_MACHINE = 'machine/createMachine';
export const UPDATE_MACHINE = 'machine/updateMachine';
export const DELETE_MACHINE = 'machine/deleteMachine';
export const SEARCH_MACHINES = 'machine/searchMachines';
export const FETCH_STATISTICS = 'machine/fetchStatistics';
export const BULK_ACTIVATE = 'machine/bulkActivate';
export const BULK_DEACTIVATE = 'machine/bulkDeactivate';
export const BULK_DELETE = 'machine/bulkDelete';
export const EXPORT_TO_CSV = 'machine/exportToCSV';

// Saga action creators
export const fetchMachines = (params?: MachineSearchParams) => ({ type: FETCH_MACHINES, payload: params });
export const fetchMachineById = (id: number) => ({ type: FETCH_MACHINE_BY_ID, payload: id });
export const createMachine = (machine: Omit<Machine, 'Id'>) => ({ type: CREATE_MACHINE, payload: machine });
export const updateMachine = (id: number, machine: Partial<Machine>) => ({ type: UPDATE_MACHINE, payload: { id, machine } });
export const deleteMachine = (id: number) => ({ type: DELETE_MACHINE, payload: id });
export const searchMachines = (params: MachineSearchParams) => ({ type: SEARCH_MACHINES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: MachineSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default machineSlice.reducer; 